import { Loading } from '@affine/component';
import type { GitHubRepository } from '@affine/core/modules/integration/type';
import { DoneIcon, LockIcon, SearchIcon } from '@blocksuite/icons/rc';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as styles from './styles.css';

// One search field over a filterable list, with ↑/↓/Enter from the search box
export const RepoPicker = ({
  id,
  repos,
  loading,
  selected,
  onSelect,
}: {
  id: string;
  repos: GitHubRepository[];
  loading: boolean;
  selected: GitHubRepository | null;
  onSelect: (repo: GitHubRepository) => void;
}) => {
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return repos;
    return repos.filter(repo => repo.full_name.toLowerCase().includes(query));
  }, [repos, search]);

  useEffect(() => {
    setActiveIndex(0);
  }, [search]);

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        // Enter picks a repo here; it must not submit the dialog
        e.preventDefault();
        e.stopPropagation();
        const repo = filtered[activeIndex];
        if (repo) onSelect(repo);
      }
    },
    [filtered, activeIndex, onSelect]
  );

  const listId = `${id}-list`;
  const activeRepo = filtered[activeIndex];

  return (
    <div className={styles.picker}>
      <div className={styles.pickerSearch}>
        <SearchIcon className={styles.pickerSearchIcon} />
        <input
          id={id}
          type="text"
          className={styles.pickerSearchInput}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search repositories"
          autoComplete="off"
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={
            activeRepo ? `${listId}-${activeRepo.id}` : undefined
          }
        />
      </div>
      {loading ? (
        <div className={styles.pickerEmpty}>
          <Loading size={16} />
          Loading repositories
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.pickerEmpty}>
          {search ? 'No matching repositories' : 'No repositories'}
        </div>
      ) : (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Repositories"
          className={styles.pickerList}
        >
          {filtered.map((repo, index) => {
            const isSelected = selected?.id === repo.id;
            return (
              <li
                key={repo.id}
                id={`${listId}-${repo.id}`}
                data-index={index}
                role="option"
                aria-selected={isSelected}
                data-active={index === activeIndex}
                className={styles.pickerItem}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => onSelect(repo)}
                title={repo.description ?? repo.full_name}
              >
                <span className={styles.pickerItemText}>
                  <span className={styles.pickerOwner}>
                    {repo.owner.login}/
                  </span>
                  <span className={styles.pickerItemName}>{repo.name}</span>
                </span>
                {repo.private && (
                  <LockIcon
                    className={styles.pickerItemIcon}
                    aria-label="Private"
                  />
                )}
                {isSelected && <DoneIcon className={styles.pickerCheck} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
